import React, { useCallback, useEffect, useState } from 'react';
import { useLeague } from '@shootout-comps/core';
import type { RosterPlayerHistory } from '@shootout-comps/core';

interface PlayerHistoryPageProps {
  leagueId: string;
  divisionId: string;
  teamId: string;
  playerId: string;
  onBack: () => void;
  onSelectFixture: (fixtureId: string) => void;
}

export default function PlayerHistoryPage({ leagueId, divisionId, teamId, playerId, onBack, onSelectFixture }: PlayerHistoryPageProps) {
  const { fetchRosterPlayerHistory } = useLeague();
  const [history, setHistory] = useState<RosterPlayerHistory | null>(null);
  const [error, setError] = useState('');

  const reload = useCallback(() => {
    fetchRosterPlayerHistory(leagueId, divisionId, teamId, playerId).then(setHistory).catch(e => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [fetchRosterPlayerHistory, leagueId, divisionId, teamId, playerId]);

  useEffect(reload, [reload]);

  if (error && !history) return <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '1rem', color: '#e74c3c', textAlign: 'center' }}>{error}</div>;
  if (!history) return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--chalk-dim)' }}>Loading…</div>;

  const played = history.matches.length;
  const won = history.matches.filter(m => m.won === true).length;
  const lost = history.matches.filter(m => m.won === false).length;
  const careerHighBreak = history.matches.reduce((max, m) => Math.max(max, m.highBreak), 0);

  return (
    <div style={{ maxWidth: '640px', margin: '0 auto', padding: '2rem 1.5rem' }}>
      <button onClick={onBack} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '1.25rem' }}>
        ← {history.team.name}
      </button>

      <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.5rem,5vw,2rem)', color: 'var(--gold)', marginBottom: '0.2rem' }}>
        {history.player.name}
      </h2>
      <p style={{ color: 'var(--chalk-dim)', fontSize: '0.8rem', marginBottom: '1.25rem' }}>hcp {history.player.handicap}</p>

      <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {([
          ['Played', played],
          ['Won', won],
          ['Lost', lost],
          ['High Break', careerHighBreak],
        ] as const).map(([label, value]) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'Bebas Neue', fontSize: '1.6rem', color: 'var(--gold)', lineHeight: 1 }}>{value}</div>
            <div style={{ fontSize: '0.65rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '0.2rem' }}>{label}</div>
          </div>
        ))}
      </div>

      {error && <p style={{ color: '#e74c3c', fontSize: '0.8rem', marginBottom: '1rem' }}>{error}</p>}

      {history.upcomingFixtures.length > 0 && (
        <div style={{ marginBottom: '1.75rem' }}>
          <h3 style={{ fontFamily: 'Bebas Neue', fontSize: '1.1rem', color: 'var(--chalk)', marginBottom: '0.6rem', letterSpacing: '0.02em' }}>Fixtures</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {history.upcomingFixtures.map(f => (
              <button
                key={f.fixtureId}
                onClick={() => onSelectFixture(f.fixtureId)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '0.75rem 1rem', textAlign: 'left', cursor: 'pointer', color: 'inherit', font: 'inherit' }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>
                    vs {f.opponentTeamName} <span style={{ color: 'var(--chalk-dim)', fontWeight: 400 }}>{f.isHome ? '' : '— away'}</span>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', marginTop: '0.2rem' }}>
                    {f.scheduledAt ? new Date(f.scheduledAt).toLocaleDateString() : 'Date TBC'}
                    {f.venue ? ` · ${f.venue}` : ''}
                  </div>
                </div>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: f.status === 'lineup_set' ? '#3498db' : f.status === 'in_progress' ? '#2ecc71' : 'var(--chalk-dim)', textTransform: 'uppercase', flexShrink: 0 }}>
                  {f.status === 'lineup_set' ? 'Lineup Set' : f.status === 'in_progress' ? 'In Progress' : 'Scheduled'}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {history.matches.length === 0 ? (
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>No completed matches yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {history.matches.map(m => (
            <div key={m.frameMatchId} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '0.75rem 1rem' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>
                  vs {m.opponentName} <span style={{ color: 'var(--chalk-dim)', fontWeight: 400 }}>({m.opponentTeamName}{m.isHome ? '' : ' — away'})</span>
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', marginTop: '0.2rem' }}>
                  {m.completedAt ? new Date(m.completedAt).toLocaleDateString() : m.scheduledAt ? new Date(m.scheduledAt).toLocaleDateString() : ''}
                  {m.highBreak > 0 ? ` · High break: ${m.highBreak}` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontFamily: 'Bebas Neue', fontSize: '1.1rem', color: m.won === true ? '#2ecc71' : m.won === false ? '#e74c3c' : 'var(--chalk-dim)' }}>
                  {m.scoreFor ?? '—'}–{m.scoreAgainst ?? '—'}
                </div>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: m.won === true ? '#2ecc71' : m.won === false ? '#e74c3c' : 'var(--chalk-dim)', textTransform: 'uppercase' }}>
                  {m.won === true ? 'Won' : m.won === false ? 'Lost' : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
